import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Toast } from "primereact/toast";
import {
  FaPlus,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaThumbtack,
  FaThumbsUp,
  FaCommentDots,
  FaEdit,
  FaTrash,
  FaYoutube,
  FaImage,
  FaAlignLeft,
  FaPlay,
  FaExternalLinkAlt,
  FaBell,
  FaCloudUploadAlt,
  FaExclamationCircle,
  FaInbox,
  FaReply,
  FaMobileAlt,
  FaNewspaper,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes opFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes opFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes opSlide { from { transform: translateX(100%); } to { transform: none; } }
@keyframes opZoom { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
.op-in { animation: opFadeUp .45s cubic-bezier(.2,.7,.2,1) both; }
.op-fade { animation: opFade .2s ease both; }
.op-slide { animation: opSlide .3s cubic-bezier(.2,.8,.2,1) both; }
.op-zoom { animation: opZoom .22s cubic-bezier(.2,.8,.2,1) both; }
.op-press { transition: transform .15s ease, box-shadow .2s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.op-press:hover { transform: translateY(-1px); }
.op-press:active { transform: scale(.96); }
.op-card { transition: transform .2s ease, box-shadow .25s ease, border-color .2s ease; }
.op-card:hover { transform: translateY(-3px); box-shadow: 0 16px 30px -16px rgba(15,23,42,.35); }
.op-card .op-play { transition: transform .2s ease; }
.op-card:hover .op-play { transform: scale(1.12); }
.op-clamp2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.op-clamp3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
@media (prefers-reduced-motion: reduce) {
  .op-in, .op-fade, .op-slide, .op-zoom { animation: none; }
  .op-press:hover, .op-press:active, .op-card:hover, .op-card:hover .op-play { transform: none; }
}
`;

const CARD =
  "bg-white dark:bg-secondary-dark-bg rounded-xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500";

/* ================= helpers ================= */

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const parseDate = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) =>
  d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const fmtTime = (d) =>
  d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";

const isPinned = (v) => v === true || v === 1 || v === "1" || v === "true";

// Works with watch, youtu.be, shorts, live, embed and mobile links
const extractYoutubeId = (url) => {
  if (!url) return null;
  const u = String(url).trim();
  const patterns = [
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  for (const p of patterns) {
    const m = u.match(p);
    if (m) return m[1];
  }
  return null;
};
const isValidYoutubeUrl = (url) => extractYoutubeId(url) !== null;
const ytThumb = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

const Avatar = ({ src, name, size = 36 }) => {
  const [err, setErr] = useState(false);
  const initials =
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  if (src && !err)
    return (
      <img
        src={src}
        alt=""
        onError={() => setErr(true)}
        className="rounded-full object-cover shrink-0 bg-slate-100"
        style={{ width: size, height: size }}
      />
    );
  return (
    <span
      className="rounded-full shrink-0 flex items-center justify-center bg-slate-200 text-slate-600 font-semibold dark:bg-slate-600 dark:text-slate-200"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials}
    </span>
  );
};

/* Post as it appears in the app feed (used for cards and live preview) */
const PostMedia = ({ image, videoId, rounded = "rounded-lg" }) => {
  if (videoId) {
    return (
      <div className={`relative overflow-hidden bg-black ${rounded}`} style={{ aspectRatio: "16/9" }}>
        <img src={ytThumb(videoId)} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        <span className="op-play absolute inset-0 m-auto w-12 h-12 rounded-full bg-red-600 text-white flex items-center justify-center shadow-lg">
          <FaPlay className="ml-0.5" size={14} />
        </span>
      </div>
    );
  }
  if (image) {
    return (
      <div className={`relative overflow-hidden bg-slate-100 dark:bg-slate-700 ${rounded}`} style={{ aspectRatio: "16/9" }}>
        <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      </div>
    );
  }
  return null;
};

/* ================= page ================= */

const AdminPost = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | pinned | video | image | text
  const [sortBy, setSortBy] = useState("default");

  // editor
  const [visible, setVisible] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({ title: "", post_text: "", youtube_video: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // activity
  const [activityVisible, setActivityVisible] = useState(false);
  const [activityPost, setActivityPost] = useState(null);
  const [postActivity, setPostActivity] = useState({ like: [], comment: [] });
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [currentPostId, setCurrentPostId] = useState(null);
  const [activityTab, setActivityTab] = useState("comments");
  const [likeQuery, setLikeQuery] = useState("");

  // confirm + busy
  const [confirm, setConfirm] = useState(null); // {type:'post'|'comment', id, title}
  const [working, setWorking] = useState(false);
  const [pinBusy, setPinBusy] = useState(null);

  const toast = useRef(null);
  const fileRef = useRef(null);
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  const cfg = { withCredentials: true };

  /* ----- list (same API) ----- */

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/admin_post.php?action=list`, { withCredentials: true });
      const postsArray = Array.isArray(res.data)
        ? res.data
        : res.data.posts || res.data.list || res.data.data || [];
      setPosts(Array.isArray(postsArray) ? postsArray : []);
    } catch (err) {
      console.error(err);
      setPosts([]);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not load posts" });
    }
    setLoading(false);
  }, [baseUrl]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  /* ----- derived ----- */

  const stats = useMemo(
    () => ({
      total: posts.length,
      pinned: posts.filter((p) => isPinned(p.pin_post)).length,
      likes: posts.reduce((s, p) => s + Number(p.total_like || 0), 0),
      comments: posts.reduce((s, p) => s + Number(p.total_comment || 0), 0),
    }),
    [posts]
  );

  const visiblePosts = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = posts.filter((p) => {
      const hasVideo = !!extractYoutubeId(p.youtube_video) || !!p.youtube_video;
      if (filter === "pinned" && !isPinned(p.pin_post)) return false;
      if (filter === "video" && !hasVideo) return false;
      if (filter === "image" && !p.post_img) return false;
      if (filter === "text" && (hasVideo || p.post_img)) return false;
      if (!q) return true;
      return [p.post_id, p.title, p.post_text, p.youtube_video]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(q));
    });
    if (sortBy !== "default") {
      list = [...list].sort((a, b) => {
        if (sortBy === "likes") return Number(b.total_like || 0) - Number(a.total_like || 0);
        if (sortBy === "comments") return Number(b.total_comment || 0) - Number(a.total_comment || 0);
        const da = parseDate(a.date_time)?.getTime() || 0;
        const db = parseDate(b.date_time)?.getTime() || 0;
        return sortBy === "oldest" ? da - db : db - da;
      });
    }
    return list;
  }, [posts, query, filter, sortBy]);

  /* ----- editor ----- */

  const resetForm = () => {
    setFormData({ title: "", post_text: "", youtube_video: "" });
    setSelectedFile(null);
    setImagePreview(null);
    setEditingPost(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const openAdd = () => {
    resetForm();
    setVisible(true);
  };

  const openEdit = (rowData) => {
    setEditingPost(rowData);
    setFormData({
      title: rowData.title || "",
      post_text: rowData.post_text || "",
      youtube_video: rowData.youtube_video || "",
    });
    setSelectedFile(null);
    setImagePreview(rowData.post_img || null);
    setVisible(true);
  };

  const closeEditor = () => {
    if (saving) return;
    setVisible(false);
    resetForm();
  };

  const setImageFile = (file) => {
    if (!file) return;
    const ok = /\.(jpe?g|png|webp)$/i.test(file.name) && file.type.startsWith("image/");
    if (!ok) {
      toast.current?.show({ severity: "error", summary: "Wrong file", detail: "Use a JPG, PNG or WebP image" });
      return;
    }
    setSelectedFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const onImageChange = (e) => setImageFile(e.target.files[0]);

  const savePost = async () => {
    const hasTitle = !!formData.title.trim();
    const hasText = !!formData.post_text.trim();
    const hasVideo = !!formData.youtube_video.trim();
    const hasImage = !!imagePreview;

    if (!hasTitle && !hasText && !hasVideo && !hasImage) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Post cannot be empty. Please fill at least one field (Title, Text, Video, or Image).",
      });
      return;
    }

    if (formData.youtube_video && !isValidYoutubeUrl(formData.youtube_video)) {
      toast.current?.show({ severity: "error", summary: "Validation Error", detail: "Please enter a valid YouTube URL" });
      return;
    }

    setSaving(true);
    try {
      const data = new FormData();
      data.append("title", formData.title.trim());
      data.append("post_text", formData.post_text.trim());
      data.append("youtube_video", formData.youtube_video.trim());
      if (selectedFile) data.append("image", selectedFile);

      const opts = { headers: { "Content-Type": "multipart/form-data" }, withCredentials: true };

      if (editingPost) {
        data.append("post_id", editingPost.post_id);
        await axios.post(`${baseUrl}/admin_post.php?action=update`, data, opts);
        toast.current?.show({ severity: "success", summary: "Updated", detail: "Post updated successfully" });
      } else {
        await axios.post(`${baseUrl}/admin_post.php?action=add`, data, opts);
        toast.current?.show({
          severity: "success",
          summary: "Published",
          detail: "Post added and notification sent to users",
        });
      }
      setVisible(false);
      resetForm();
      fetchPosts();
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to save post" });
    }
    setSaving(false);
  };

  // Ctrl+Enter saves, Esc closes
  useEffect(() => {
    const onKey = (e) => {
      if (confirm) {
        if (e.key === "Escape" && !working) setConfirm(null);
        return;
      }
      if (visible) {
        if (e.key === "Escape") closeEditor();
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) savePost();
        return;
      }
      if (activityVisible && e.key === "Escape") setActivityVisible(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  /* ----- pin post (same API) ----- */

  const togglePin = async (postId, currentPinStatus) => {
    setPinBusy(postId);
    try {
      const newStatus = isPinned(currentPinStatus) ? 0 : 1;
      const data = new FormData();
      data.append("post_id", postId);
      data.append("pin_post", newStatus);
      const res = await axios.post(`${baseUrl}/admin_post.php?action=pin_post`, data, cfg);
      if (res.data.success) {
        toast.current?.show({ severity: "success", summary: "Success", detail: res.data.message });
        fetchPosts();
      } else {
        toast.current?.show({ severity: "error", summary: "Error", detail: res.data.message || "Failed to update pin status" });
      }
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Something went wrong" });
    }
    setPinBusy(null);
  };

  /* ----- activity (same API + same nesting) ----- */

  const fetchPostActivity = async (postId, post) => {
    setLoadingActivity(true);
    setCurrentPostId(postId);
    if (post) setActivityPost(post);
    setActivityVisible(true);
    try {
      const data = new FormData();
      data.append("post_id", postId);
      const res = await axios.post(`${baseUrl}/admin_post.php?action=post_activity`, data, cfg);
      if (res.data.success) {
        const rawComments = res.data.comment || [];
        const commentMap = {};
        rawComments.forEach((c) => {
          commentMap[String(c.comment_id)] = { ...c, replies: [] };
        });
        const isReplyOf = (c) => {
          const rid = c.comment_replay ? String(c.comment_replay) : "";
          return rid !== "" && rid !== "0" && rid !== "null" && rid !== "undefined" ? rid : null;
        };
        const roots = [];
        rawComments.forEach((c) => {
          const rid = isReplyOf(c);
          if (rid && commentMap[rid]) commentMap[rid].replies.push(commentMap[String(c.comment_id)]);
          else roots.push(commentMap[String(c.comment_id)]);
        });
        setPostActivity({ like: res.data.like || [], comment: roots });
      } else {
        toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load activity" });
      }
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Something went wrong" });
    } finally {
      setLoadingActivity(false);
    }
  };

  const togglePinComment = async (commentId, currentPinStatus) => {
    try {
      const newStatus = isPinned(currentPinStatus) ? 0 : 1;
      const data = new FormData();
      data.append("comment_id", commentId);
      data.append("pin_comment", newStatus);
      const res = await axios.post(`${baseUrl}/admin_post.php?action=pin_comment`, data, cfg);
      if (res.data.success) {
        toast.current?.show({ severity: "success", summary: "Success", detail: res.data.message });
        fetchPostActivity(currentPostId);
      } else {
        toast.current?.show({ severity: "error", summary: "Error", detail: res.data.message || "Failed to update comment status" });
      }
    } catch (err) {
      console.error(err);
    }
  };

  /* ----- deletes (same APIs, custom confirm) ----- */

  const runConfirm = async () => {
    if (!confirm) return;
    setWorking(true);
    try {
      if (confirm.type === "post") {
        await axios.post(`${baseUrl}/admin_post.php?action=delete`, { post_id: confirm.id }, cfg);
        toast.current?.show({ severity: "warn", summary: "Deleted", detail: "Post deleted" });
        if (activityVisible && String(currentPostId) === String(confirm.id)) setActivityVisible(false);
        fetchPosts();
      } else {
        const data = new FormData();
        data.append("comment_id", confirm.id);
        const res = await axios.post(`${baseUrl}/admin_post.php?action=delete_comment`, data, cfg);
        if (res.data.success) {
          toast.current?.show({ severity: "warn", summary: "Deleted", detail: "Comment deleted successfully" });
          fetchPostActivity(currentPostId);
          fetchPosts();
        } else {
          toast.current?.show({ severity: "error", summary: "Error", detail: res.data.message || "Failed to delete comment" });
        }
      }
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Something went wrong" });
    }
    setWorking(false);
    setConfirm(null);
  };

  /* ----- UI pieces ----- */

  const statCards = [
    { label: "Total posts", value: stats.total, icon: FaNewspaper, tint: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300" },
    { label: "Pinned", value: stats.pinned, icon: FaThumbtack, tint: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300" },
    { label: "Total likes", value: stats.likes, icon: FaThumbsUp, tint: "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300" },
    { label: "Total comments", value: stats.comments, icon: FaCommentDots, tint: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300" },
  ];

  const filters = [
    { id: "all", label: "All" },
    { id: "pinned", label: "Pinned", icon: FaThumbtack },
    { id: "video", label: "Video", icon: FaYoutube },
    { id: "image", label: "Image", icon: FaImage },
    { id: "text", label: "Text only", icon: FaAlignLeft },
  ];

  const formVideoId = extractYoutubeId(formData.youtube_video);
  const filteredLikes = postActivity.like.filter((l) =>
    String(l.user_name || "").toLowerCase().includes(likeQuery.trim().toLowerCase())
  );
  const totalCommentCount = postActivity.comment.reduce((s, c) => s + 1 + (c.replies?.length || 0), 0);

  const CommentItem = ({ c, isReply }) => {
    const d = parseDate(c.date_time);
    const pinned = isPinned(c.pin_comment);
    return (
      <div
        className={`flex gap-3 rounded-lg p-3 ${
          pinned
            ? "bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800"
            : isReply
            ? "bg-slate-50 dark:bg-slate-800/60"
            : "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
        }`}
      >
        <Avatar src={c.profile_img} name={c.user_name} size={isReply ? 30 : 36} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {c.user_id ? (
              <Link
                to={`/user-profile/${c.user_id}`}
                className="text-sm font-semibold text-slate-900 dark:text-white hover:text-cyan-700 truncate"
              >
                {c.user_name || "User"}
              </Link>
            ) : (
              <span className="text-sm font-semibold text-slate-900 dark:text-white">{c.user_name || "User"}</span>
            )}
            {pinned && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
                <FaThumbtack size={9} /> Pinned
              </span>
            )}
            <span className="ml-auto text-xs text-slate-400 whitespace-nowrap">
              {fmtDate(d)}, {fmtTime(d)}
            </span>
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-200 mt-1 whitespace-pre-wrap break-words">{c.comment}</p>
          <div className="flex gap-1 mt-2">
            {!isReply && (
              <button
                type="button"
                onClick={() => togglePinComment(c.comment_id, c.pin_comment)}
                className={`op-press inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium ${
                  pinned
                    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                    : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                }`}
              >
                <FaThumbtack size={10} /> {pinned ? "Unpin" : "Pin"}
              </button>
            )}
            <button
              type="button"
              onClick={() => setConfirm({ type: "comment", id: c.comment_id, title: isReply ? "reply" : "comment" })}
              className="op-press inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30"
            >
              <FaTrash size={10} /> Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ================= render ================= */

  return (
    <div
      style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}}
      className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen"
    >
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="op-in flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Owner Post</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Owner Posts</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Posts from YCNP shown to every user in the app feed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={fetchPosts}
            disabled={loading}
            className="op-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 disabled:opacity-60"
          >
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="op-press inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold"
          >
            <FaPlus size={12} /> Add Post
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className={`${CARD} op-in p-4 flex items-center gap-4`} style={{ animationDelay: `${i * 60}ms` }}>
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.tint}`}>
                <Icon />
              </span>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{c.label}</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">{fmt(c.value)}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className={`${CARD} op-in mt-4 p-3 flex flex-wrap items-center gap-3`} style={{ animationDelay: "240ms" }}>
        <div className="relative flex-1 min-w-[220px]">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by ID, title, text or video link"
            className={`${INPUT} h-10 pl-9 pr-9`}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700"
              aria-label="Clear search"
            >
              <FaTimes size={12} />
            </button>
          )}
        </div>
        <div className="inline-flex flex-wrap p-1 rounded-lg bg-slate-100 dark:bg-slate-700">
          {filters.map((f) => {
            const Icon = f.icon;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all active:scale-95 ${
                  filter === f.id
                    ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm"
                    : "text-slate-500 dark:text-slate-300 hover:text-slate-800"
                }`}
              >
                {Icon && <Icon size={11} />} {f.label}
              </button>
            );
          })}
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className={`${INPUT} h-10 px-3 w-auto`}
        >
          <option value="default">Pinned first (app order)</option>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="likes">Most liked</option>
          <option value="comments">Most commented</option>
        </select>
      </div>

      {/* Posts grid */}
      {loading && !posts.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={`${CARD} p-4`}>
              <div className="rounded-lg bg-slate-100 dark:bg-slate-700 animate-pulse" style={{ aspectRatio: "16/9" }} />
              <div className="h-4 w-2/3 mt-4 rounded bg-slate-100 dark:bg-slate-700 animate-pulse" />
              <div className="h-3 w-1/2 mt-2 rounded bg-slate-100 dark:bg-slate-700 animate-pulse" />
            </div>
          ))}
        </div>
      ) : visiblePosts.length === 0 ? (
        <div className={`${CARD} mt-4 px-5 py-14 text-center`}>
          <FaInbox className="mx-auto text-slate-300" size={34} />
          <p className="mt-3 font-medium text-slate-700 dark:text-slate-200">
            {posts.length ? "No posts match these filters" : "No posts yet"}
          </p>
          <p className="text-sm text-slate-500">
            {posts.length ? "Change the search or filter." : "Create the first post for your users."}
          </p>
          {!posts.length && (
            <button
              type="button"
              onClick={openAdd}
              className="op-press mt-4 inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-cyan-600 text-white text-sm font-semibold"
            >
              <FaPlus size={12} /> Add Post
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4 items-start">
          {visiblePosts.map((p, i) => {
            const vid = extractYoutubeId(p.youtube_video);
            const pinned = isPinned(p.pin_post);
            const d = parseDate(p.date_time);
            const hasContent = p.title || p.post_text;
            return (
              <article
                key={p.post_id}
                className={`op-card op-in rounded-xl border bg-white dark:bg-secondary-dark-bg overflow-hidden ${
                  pinned ? "border-amber-300 dark:border-amber-700" : "border-slate-200 dark:border-slate-700"
                }`}
                style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}
              >
                {pinned && (
                  <div className="flex items-center gap-2 px-4 py-1.5 bg-amber-50 dark:bg-amber-900/20 text-xs font-semibold text-amber-700 dark:text-amber-300">
                    <FaThumbtack size={10} /> Pinned to top of feed
                  </div>
                )}

                <div className="p-4">
                  <div className="flex items-center justify-between gap-2 mb-3 text-xs text-slate-500 dark:text-slate-400">
                    <span className="font-semibold text-slate-700 dark:text-slate-200">#{p.post_id}</span>
                    <span>
                      {fmtDate(d)}, {fmtTime(d)}
                    </span>
                  </div>

                  {(vid || p.post_img) && (
                    <button
                      type="button"
                      onClick={() =>
                        vid
                          ? window.open(`https://www.youtube.com/watch?v=${vid}`, "_blank")
                          : window.open(p.post_img, "_blank")
                      }
                      className="block w-full text-left"
                      title={vid ? "Play video on YouTube" : "Open image"}
                    >
                      <PostMedia image={p.post_img} videoId={vid} />
                    </button>
                  )}

                  {vid && p.post_img && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                      <img src={p.post_img} alt="" className="w-10 h-10 rounded object-cover" />
                      Also has an image
                    </div>
                  )}

                  {!vid && p.youtube_video && (
                    <a
                      href={p.youtube_video}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1.5 text-sm text-cyan-700 hover:underline"
                    >
                      Open Link <FaExternalLinkAlt size={10} />
                    </a>
                  )}

                  {hasContent ? (
                    <div className="mt-3">
                      {p.title && (
                        <h3 className="op-clamp2 font-semibold text-slate-900 dark:text-white">{p.title}</h3>
                      )}
                      {p.post_text && (
                        <p className="op-clamp3 text-sm text-slate-600 dark:text-slate-300 mt-1 whitespace-pre-line">
                          {p.post_text}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-slate-400 italic">
                      {vid ? "Video post (no title or text)" : p.post_img ? "Image post (no title or text)" : "Empty post"}
                    </p>
                  )}

                  {/* Interactions */}
                  <button
                    type="button"
                    onClick={() => fetchPostActivity(p.post_id, p)}
                    className="op-press mt-4 w-full flex items-center gap-4 rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2 text-sm hover:bg-cyan-50 dark:hover:bg-cyan-900/20"
                    title="View likes and comments"
                  >
                    <span className="inline-flex items-center gap-1.5 text-slate-700 dark:text-slate-200">
                      <FaThumbsUp className="text-blue-500" /> <b className="tabular-nums">{fmt(p.total_like)}</b> likes
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-slate-700 dark:text-slate-200">
                      <FaCommentDots className="text-emerald-500" /> <b className="tabular-nums">{fmt(p.total_comment)}</b> comments
                    </span>
                    <span className="ml-auto text-xs font-medium text-cyan-700 dark:text-cyan-400">View</span>
                  </button>
                </div>

                {/* Actions */}
                <div className="flex border-t border-slate-100 dark:border-slate-700 divide-x divide-slate-100 dark:divide-slate-700">
                  <button
                    type="button"
                    onClick={() => openEdit(p)}
                    className="flex-1 h-11 inline-flex items-center justify-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 active:bg-slate-100 transition-colors"
                  >
                    <FaEdit size={12} /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => togglePin(p.post_id, p.pin_post)}
                    disabled={pinBusy === p.post_id}
                    title={pinned ? "Unpin this post" : "Pin this post to top"}
                    className={`flex-1 h-11 inline-flex items-center justify-center gap-2 text-sm font-medium transition-colors disabled:opacity-60 ${
                      pinned
                        ? "text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                        : "text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                    }`}
                  >
                    {pinBusy === p.post_id ? <FaSyncAlt className="animate-spin" size={11} /> : <FaThumbtack size={11} />}
                    {pinned ? "Unpin" : "Pin"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirm({ type: "post", id: p.post_id, title: p.title || `Post #${p.post_id}` })}
                    className="flex-1 h-11 inline-flex items-center justify-center gap-2 text-sm font-medium text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                  >
                    <FaTrash size={11} /> Delete
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* ================= Editor ================= */}
      {visible && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="op-fade absolute inset-0 bg-slate-900/40" onClick={closeEditor} />
          <aside className="op-slide relative w-full max-w-4xl h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <div>
                <p className="text-xs text-slate-500">{editingPost ? `Post #${editingPost.post_id}` : "New post"}</p>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {editingPost ? "Edit Post" : "Add Post"}
                </h2>
              </div>
              <button
                type="button"
                onClick={closeEditor}
                className="op-press w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 p-5">
                {/* Form */}
                <div className="lg:col-span-3 space-y-5">
                  <div>
                    <div className="flex justify-between mb-1.5">
                      <label htmlFor="title" className="text-sm font-medium text-slate-700 dark:text-slate-200">Title</label>
                      <span className="text-xs text-slate-400 tabular-nums">{formData.title.length}</span>
                    </div>
                    <input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Short headline for the post"
                      className={`${INPUT} h-11 px-3`}
                    />
                  </div>

                  <div>
                    <div className="flex justify-between mb-1.5">
                      <label htmlFor="post_text" className="text-sm font-medium text-slate-700 dark:text-slate-200">Post Text</label>
                      <span className="text-xs text-slate-400 tabular-nums">{formData.post_text.length}</span>
                    </div>
                    <textarea
                      id="post_text"
                      rows={6}
                      value={formData.post_text}
                      onChange={(e) => setFormData({ ...formData, post_text: e.target.value })}
                      placeholder="Write the message for your users"
                      className={`${INPUT} px-3 py-2.5 resize-y`}
                    />
                  </div>

                  <div>
                    <label htmlFor="youtube_video" className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                      YouTube Video URL <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <div className="relative">
                      <FaYoutube className="absolute left-3 top-1/2 -translate-y-1/2 text-red-500" />
                      <input
                        id="youtube_video"
                        value={formData.youtube_video}
                        onChange={(e) => setFormData({ ...formData, youtube_video: e.target.value })}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className={`${INPUT} h-11 pl-9 pr-9 ${
                          formData.youtube_video && !formVideoId ? "border-rose-300 focus:ring-rose-400" : ""
                        }`}
                      />
                      {formData.youtube_video && (
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, youtube_video: "" })}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700"
                          aria-label="Clear video link"
                        >
                          <FaTimes size={12} />
                        </button>
                      )}
                    </div>
                    {formData.youtube_video && !formVideoId && (
                      <p className="text-xs text-rose-600 mt-1">Please enter a valid YouTube URL</p>
                    )}
                    {formVideoId && (
                      <p className="text-xs text-emerald-600 mt-1">Video found. See the preview on the right.</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                      Post Image <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <input ref={fileRef} type="file" id="image" accept="image/jpeg,image/png,image/webp" onChange={onImageChange} className="hidden" />
                    {imagePreview ? (
                      <div className="relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                        <img src={imagePreview} alt="Preview" className="w-full max-h-56 object-contain" />
                        <div className="absolute top-2 right-2 flex gap-2">
                          <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            className="op-press h-8 px-3 rounded-md bg-white/95 text-slate-800 text-xs font-semibold shadow"
                          >
                            Replace
                          </button>
                          {(!editingPost || selectedFile) && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedFile(null);
                                setImagePreview(editingPost?.post_img || null);
                                if (fileRef.current) fileRef.current.value = "";
                              }}
                              className="op-press h-8 w-8 rounded-md bg-white/95 text-rose-600 flex items-center justify-center shadow"
                              aria-label="Remove image"
                            >
                              <FaTimes size={12} />
                            </button>
                          )}
                        </div>
                        {editingPost && !selectedFile && (
                          <p className="px-3 py-2 text-xs text-slate-500 border-t border-slate-200 dark:border-slate-700">
                            Current image. Use Replace to change it.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => fileRef.current?.click()}
                        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && fileRef.current?.click()}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragOver(false);
                          setImageFile(e.dataTransfer.files[0]);
                        }}
                        className={`op-press cursor-pointer rounded-xl border-2 border-dashed p-6 text-center ${
                          dragOver
                            ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20"
                            : "border-slate-300 dark:border-slate-600 hover:border-cyan-400"
                        }`}
                      >
                        <FaCloudUploadAlt className="mx-auto text-cyan-600" size={26} />
                        <p className="mt-2 text-sm font-medium text-slate-800 dark:text-slate-100">Drag an image here or click to choose</p>
                        <p className="text-xs text-slate-500">JPG, PNG or WebP</p>
                      </div>
                    )}
                  </div>

                  {!editingPost && (
                    <div className="flex gap-3 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 p-3 text-sm text-cyan-900 dark:text-cyan-100">
                      <FaBell className="mt-0.5 shrink-0" />
                      <span>Publishing sends a notification to all app users. Check the preview before you publish.</span>
                    </div>
                  )}
                </div>

                {/* Live preview */}
                <div className="lg:col-span-2">
                  <div className="lg:sticky lg:top-0">
                    <p className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                      <FaMobileAlt className="text-cyan-600" /> Preview in app
                    </p>
                    <div className="mx-auto max-w-[300px] rounded-[26px] border-[6px] border-slate-800 bg-slate-800 p-2 shadow-lg">
                      <div className="rounded-2xl bg-slate-100 dark:bg-slate-900 p-2 min-h-[320px]">
                        <div className="h-2 w-16 mx-auto rounded-full bg-slate-300 dark:bg-slate-700 mb-3" />
                        <div className="rounded-xl bg-white dark:bg-slate-800 p-3 shadow-sm">
                          <div className="flex items-center gap-2 mb-2">
                            <img src="/ycnp.png" alt="" className="w-8 h-8 rounded-full object-contain bg-white border border-slate-200" />
                            <div>
                              <p className="text-xs font-semibold text-slate-900 dark:text-white">YCNP</p>
                              <p className="text-[10px] text-slate-500">{editingPost ? fmtDate(parseDate(editingPost.date_time)) : "Just now"}</p>
                            </div>
                          </div>
                          {formData.title.trim() && (
                            <p className="text-sm font-semibold text-slate-900 dark:text-white break-words">{formData.title}</p>
                          )}
                          {formData.post_text.trim() && (
                            <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 whitespace-pre-line break-words">
                              {formData.post_text}
                            </p>
                          )}
                          {(formVideoId || imagePreview) && (
                            <div className="mt-2 space-y-2">
                              {imagePreview && (
                                <img src={imagePreview} alt="" className="w-full rounded-md object-cover" />
                              )}
                              {formVideoId && <PostMedia videoId={formVideoId} rounded="rounded-md" />}
                            </div>
                          )}
                          {!formData.title.trim() && !formData.post_text.trim() && !formVideoId && !imagePreview && (
                            <p className="text-xs text-slate-400 italic py-6 text-center">Start typing to see the post</p>
                          )}
                          <div className="flex gap-4 mt-3 pt-2 border-t border-slate-100 dark:border-slate-700 text-[11px] text-slate-500">
                            <span className="inline-flex items-center gap-1"><FaThumbsUp /> Like</span>
                            <span className="inline-flex items-center gap-1"><FaCommentDots /> Comment</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <span className="hidden sm:inline text-xs text-slate-500">Tip: press Ctrl + Enter to save</span>
              <div className="flex gap-2 ml-auto">
                <button
                  type="button"
                  onClick={closeEditor}
                  disabled={saving}
                  className="op-press h-10 px-4 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={savePost}
                  disabled={saving}
                  className="op-press inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-60"
                >
                  {saving && <FaSyncAlt className="animate-spin" size={12} />}
                  {saving ? "Saving..." : editingPost ? "Save changes" : "Publish post"}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* ================= Activity drawer ================= */}
      {activityVisible && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="op-fade absolute inset-0 bg-slate-900/40" onClick={() => setActivityVisible(false)} />
          <aside className="op-slide relative w-full max-w-lg h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-slate-500">Post Activity</p>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white truncate">
                    {activityPost?.title || `Post #${currentPostId}`}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => setActivityVisible(false)}
                  className="op-press w-9 h-9 shrink-0 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
              </div>
              <div className="mt-3 inline-flex p-1 rounded-lg bg-slate-100 dark:bg-slate-700">
                {[
                  { id: "comments", label: `Comments (${totalCommentCount})`, icon: FaCommentDots },
                  { id: "likes", label: `Likes (${postActivity.like.length})`, icon: FaThumbsUp },
                ].map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setActivityTab(t.id)}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all active:scale-95 ${
                        activityTab === t.id
                          ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm"
                          : "text-slate-500 dark:text-slate-300"
                      }`}
                    >
                      <Icon size={11} /> {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {loadingActivity ? (
                <div className="space-y-3">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-16 rounded-lg bg-slate-100 dark:bg-slate-700 animate-pulse" />
                  ))}
                </div>
              ) : activityTab === "comments" ? (
                postActivity.comment.length === 0 ? (
                  <div className="text-center py-14">
                    <FaCommentDots className="mx-auto text-slate-300" size={30} />
                    <p className="mt-3 text-slate-600 dark:text-slate-300">No comments yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {postActivity.comment.map((c) => (
                      <div key={c.comment_id} className="op-fade">
                        <CommentItem c={c} />
                        {c.replies?.length > 0 && (
                          <div className="ml-6 mt-2 pl-4 border-l-2 border-slate-200 dark:border-slate-700 space-y-2">
                            <p className="flex items-center gap-1.5 text-xs text-slate-500">
                              <FaReply size={10} /> {c.replies.length} {c.replies.length === 1 ? "reply" : "replies"}
                            </p>
                            {c.replies.map((r) => (
                              <CommentItem key={r.comment_id} c={r} isReply />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <>
                  <div className="relative mb-3">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={12} />
                    <input
                      value={likeQuery}
                      onChange={(e) => setLikeQuery(e.target.value)}
                      placeholder="Search people"
                      className={`${INPUT} h-9 pl-8`}
                    />
                  </div>
                  {filteredLikes.length === 0 ? (
                    <div className="text-center py-14">
                      <FaThumbsUp className="mx-auto text-slate-300" size={30} />
                      <p className="mt-3 text-slate-600 dark:text-slate-300">
                        {postActivity.like.length ? "No one matches this search" : "No likes yet"}
                      </p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-100 dark:divide-slate-700">
                      {filteredLikes.map((like) => {
                        const d = parseDate(like.date_time);
                        const inner = (
                          <>
                            <Avatar src={like.profile_img} name={like.user_name} size={38} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{like.user_name || "User"}</p>
                              <p className="text-xs text-slate-500">{fmtDate(d)}, {fmtTime(d)}</p>
                            </div>
                            <FaThumbsUp className="text-blue-500" size={12} />
                          </>
                        );
                        return (
                          <li key={like.like_id}>
                            {like.user_id ? (
                              <Link
                                to={`/user-profile/${like.user_id}`}
                                className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
                              >
                                {inner}
                              </Link>
                            ) : (
                              <div className="flex items-center gap-3 py-2.5">{inner}</div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
            </div>
          </aside>
        </div>
      )}

      {/* ================= Confirm dialog ================= */}
      {confirm && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="op-fade absolute inset-0 bg-slate-900/50" onClick={() => !working && setConfirm(null)} />
          <div className="op-zoom relative w-full max-w-sm rounded-xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className="w-11 h-11 rounded-full bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300 flex items-center justify-center">
              <FaExclamationCircle size={20} />
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">
              {confirm.type === "post" ? "Delete this post?" : `Delete this ${confirm.title}?`}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 break-words">
              {confirm.type === "post"
                ? `"${confirm.title}" will be removed from the app feed. This cannot be undone.`
                : "It will be removed from the post for everyone. This cannot be undone."}
            </p>
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={() => setConfirm(null)}
                disabled={working}
                className="op-press flex-1 h-10 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runConfirm}
                disabled={working}
                className="op-press flex-1 h-10 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {working ? <FaSyncAlt className="animate-spin" size={12} /> : <FaTrash size={12} />}
                {working ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPost;